import type { PromotionStatus } from '@liza/ui/components/records/RecordListItem';
import { useCallback, useEffect, useRef, useState } from 'react';

const SUCCESS_DURATION_MS = 1500;

export type UseRecordPromotionStatusResult = {
  promote: (id: string, action: () => Promise<unknown>) => Promise<void>;
  getPromotionStatus: (id: string) => PromotionStatus;
};

export const useRecordPromotionStatus = (): UseRecordPromotionStatusResult => {
  const [state, setState] = useState<Record<string, PromotionStatus>>({});
  const timeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return (): void => {
      for (const handle of timeouts.values()) clearTimeout(handle);
      timeouts.clear();
    };
  }, []);

  const clear = useCallback((id: string): void => {
    const existing = timeoutsRef.current.get(id);
    if (existing) {
      clearTimeout(existing);
      timeoutsRef.current.delete(id);
    }
    setState((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const promote = useCallback(
    async (id: string, action: () => Promise<unknown>): Promise<void> => {
      if (state[id]) return;

      setState((prev) => ({ ...prev, [id]: 'pending' }));
      try {
        await action();
        setState((prev) => ({ ...prev, [id]: 'success' }));
        const handle = setTimeout(() => clear(id), SUCCESS_DURATION_MS);
        timeoutsRef.current.set(id, handle);
      } catch {
        clear(id);
      }
    },
    [clear, state],
  );

  const getPromotionStatus = useCallback((id: string): PromotionStatus => state[id] ?? 'idle', [state]);

  return { promote, getPromotionStatus };
};
